// ============================================================
// TMR Portal Admin — as variáveis do tipo, clicáveis, com as já usadas marcadas
// ============================================================
import type { DocumentVariable } from '../../data/contractTemplates/contract';

/** Props for {@link VariablePalette}. */
interface VariablePaletteProps {
  variables: DocumentVariable[];
  /** Markers the text already uses, as the preview reported them. */
  used: string[];
  /** Inserts `{{name}}` at the cursor. Absent while the operator may not edit. */
  onInsert?: (marker: string) => void;
}

/**
 * The variables a template of this document type may use.
 *
 * **Clickable on purpose, and it is not a convenience.** A marker typed by hand and spelt wrong is not
 * refused by anything the operator can see while writing — it becomes a validation problem at publication
 * at best, and at worst `{{paciente.apelido}}` printed into a contract somebody is reading. Inserting it
 * is the only way to be sure it is one the engine can fill.
 *
 * The used/unused mark answers the other half: which of the type's variables this text has not mentioned.
 * A contract missing the patient's name is legal and useless, and nothing else on the screen says so.
 */
export function VariablePalette({ variables, used, onInsert }: VariablePaletteProps) {
  const usedSet = new Set(used);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-subtle)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        Variáveis do documento
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {variables.map((variable) => {
          const isUsed = usedSet.has(variable.name);

          return (
            <button
              key={variable.name}
              type="button"
              disabled={!onInsert}
              onClick={() => onInsert?.(variable.name)}
              title={onInsert ? 'Inserir no texto' : undefined}
              style={{
                textAlign: 'left',
                padding: '8px 10px',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                background: isUsed ? 'var(--cream-100)' : 'transparent',
                cursor: onInsert ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <code style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>
                  {`{{${variable.name}}}`}
                </code>
                {isUsed && (
                  <span style={{ fontSize: 11, color: 'var(--fg-subtle)' }}>já usada</span>
                )}
              </span>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.4 }}>
                {variable.description}
              </span>
            </button>
          );
        })}
      </div>

      <UnknownMarkers known={variables.map((variable) => variable.name)} used={used} />
    </div>
  );
}

/**
 * Markers the text uses that the document type does not have.
 *
 * Shown here rather than only among the publication's problems because this is where the operator is
 * looking while they write — and a marker the engine cannot fill is the mistake this whole panel exists to
 * prevent.
 */
function UnknownMarkers({ known, used }: { known: string[]; used: string[] }) {
  const knownSet = new Set(known);
  const unknown = used.filter((marker) => !knownSet.has(marker));

  if (unknown.length === 0) return null;

  return (
    <div style={{ fontSize: 12.5, color: 'oklch(38% 0.12 28)', lineHeight: 1.5 }}>
      <strong>Fora do catálogo:</strong>{' '}
      {unknown.map((marker) => `{{${marker}}}`).join(', ')}
      <br />
      O sistema não sabe preencher estas — o texto não pode ser publicado assim.
    </div>
  );
}
